import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { FulfillmentMethod } from '@enums/fulfillment-method';
import { Gender } from '@enums/gender';
import { Pharmacy } from '@enums/pharmacies';
import { CartAdditionalItem } from '@models/cart-additional-item';
import { ConsultationRequest } from '@models/consultation-request/consultation-request';
import { MedicalHistoryQuestion } from '@models/medical-history-question';
import { EligibleTreatmentType } from '@models/treatment-types/eligible-treatment-type.interface';
import { TreatmentGenderSettings } from '@models/treatment-types/treatment-gender-settings.model';
import { TreatmentOrderAgreements } from '@models/treatment-types/treatment-order-agreements.model';

/**
 * Keys that must be provided when constructing a {@link TreatmentType}.
 */
type RequiredTreatmentTypeKeys = 'id' | 'name';

/**
 * Constructor data for a {@link TreatmentType}: `id` and `name` are required, all other properties are optional.
 */
export type TreatmentTypeData = Required<Pick<TreatmentType, RequiredTreatmentTypeKeys>> &
  Partial<Omit<TreatmentType, RequiredTreatmentTypeKeys>>;

/**
 * Defines the configuration settings and related logic for a single treatment form type.
 */
export class TreatmentType {
  public id: string;
  public name: ConsultationTreatmentTypes;

  public eligibleTreatmentTypes: EligibleTreatmentType[] = [];
  public restrictedStates: string[] = [];
  public stateRestrictionExemptTestIds: number[] = [];
  public restrictedStatesError: string = 'This treatment is not available in your state.';
  public designatedPharmacyId: number = Pharmacy.TAHA;
  public requireHeightAndWeight: boolean = false;
  public genderSettings: TreatmentGenderSettings = new TreatmentGenderSettings();
  public requiresConsultationUpdate: boolean = true;
  public requiresInsuranceTypeSelection: boolean = false;
  public orderAgreements: TreatmentOrderAgreements = new TreatmentOrderAgreements();
  public customMedicalHistoryQuestions: MedicalHistoryQuestion[] = [];
  public defaultConsultationStatus: ConsultationStatus = ConsultationStatus.Incomplete;
  public orderPageHeaderSubtitle: string | null = null;
  public shippingService: string = '';
  public isThirdPartyFulfilled: boolean = false;
  public minAgeAllowed: number | undefined = undefined;
  public displayDoctorReviewCartItem: boolean = false;

  /**
   * Creates an instance of TreatmentType.
   *
   * @param {TreatmentTypeData} data Data used to initialize the instance. `id` and `name` are required.
   */
  public constructor(data: TreatmentTypeData) {
    Object.assign(this, data);
  }

  /**
   * Checks if a specific gender is disallowed for this treatment setting.
   *
   * @param {Gender} gender The gender to check.
   * @returns {boolean} `true` if the gender is explicitly listed in `gendersDisallowed`, `false` otherwise.
   */
  public isGenderDisallowed(gender: Gender): boolean {
    return this.genderSettings.isDisallowed(gender);
  }

  /**
   * Checks if this treatment is restricted in a specific state (case-insensitive).
   * The restriction is bypassed only when `testIds` is non-empty AND every test in it
   * is listed in `stateRestrictionExemptTestIds`. A mixed cart (any non-exempt test
   * present) keeps the restriction, since the policy is enforced per-test.
   *
   * @param {string} state The state abbreviation (e.g., 'CA') to check.
   * @param {number[]} testIds The test IDs currently in the order.
   * @returns {boolean} `true` if the state is restricted and the cart is not fully exempt, `false` otherwise.
   */
  public isStateRestricted(state: string, testIds: number[] = []): boolean {
    const restricted = this.restrictedStates?.includes(state.toUpperCase()) ?? false;

    if (!restricted) {
      return false;
    }

    const allExempt = testIds.length > 0 && testIds.every((id) => this.stateRestrictionExemptTestIds.includes(id));

    return !allExempt;
  }

  /**
   * Gets the eligible treatment types (e.g., specific products, upsells) associated with this treatment form.
   *
   * @returns {EligibleTreatmentType[]} An array of `EligibleTreatmentType` objects.
   */
  public getEligibleTreatmentTypes(): EligibleTreatmentType[] {
    return this.eligibleTreatmentTypes ?? [];
  }

  /**
   * Gets the appropriate treatment name based gender.
   *
   * @param {Gender} gender The gender to get name for.
   * @returns {ConsultationTreatmentTypes} The treatment name.
   */
  public getTreatmentNameByGender(gender: Gender): ConsultationTreatmentTypes {
    return this.genderSettings.getTreatmentName(gender, this.name);
  }

  /**
   * Gets the info message to display for a specific gender.
   *
   * @param {Gender} gender The gender to get info message for.
   * @returns {string} The info message or empty string.
   */
  public getInfoMessageByGender(gender: Gender): string {
    return this.genderSettings.getInfoMessage(gender);
  }

  /**
   * Gets medication ID for a specific gender if gender-specific medication exists.
   *
   * @param {Gender} gender The gender to get medication for.
   * @param {number} originalMedicationId The original medication ID to potentially replace.
   * @returns {number | null} The replacement medication ID or null if no mapping exists.
   */
  public getMedicationByGender(gender: Gender, originalMedicationId: number): number | null {
    return this.genderSettings.getMedication(gender, originalMedicationId);
  }

  /**
   * Gets additional items to display in the cart (order summary).
   *
   * @param {boolean} isShipped Whether the order will be shipped (true) or picked up (false).
   * @returns {CartAdditionalItem[]} Array with pharmacy fulfillment method and price.
   */
  public getCartAdditionalItems(isShipped: boolean): CartAdditionalItem[] {
    const prefix = isShipped ? '' : 'Local ';
    const items: CartAdditionalItem[] = [
      {
        description: `${prefix}Pharmacy ${isShipped ? FulfillmentMethod.Delivery : FulfillmentMethod.Pickup}`,
        price: '$0.00',
      },
    ];

    if (this.displayDoctorReviewCartItem) {
      items.push({
        description: 'Doctor Review',
        tooltip: 'Our Physicians will review your medical information and contact you with your prescription.',
        price: 'Included',
      });
    }

    return items;
  }

  /**
   * Returns true if the treatment will be shipped based on the pharmacy selected in the consultation request.
   *
   * @param {ConsultationRequest} consultationRequest the consultation request to check
   */
  public isShipped(consultationRequest: ConsultationRequest): boolean {
    return consultationRequest.pharmacy?.id === this.designatedPharmacyId;
  }
}
